import { gql, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as React from 'react';

import { Loading } from '@/imports/ui/chakra/components/Loading';

import {
  ConferenceControllerRedirect_Event,
  ConferenceControllerRedirect_EventVariables,
} from './graphql-types/ConferenceControllerRedirect_Event';

import { useConferenceContentPropertiesExist } from '../../conference/hooks/useConferenceContentPropertiesExist';

const EVENT = gql`
  query ConferenceControllerRedirect_Event($input: EventInput!) {
    event(input: $input) {
      eventV2 {
        _id
        ... on ConferenceEventV2 {
          _id
          ...useConferenceContentPropertiesExist_ConferenceEventFragment
        }
      }
    }
  }
  ${useConferenceContentPropertiesExist.conferenceEventFragment}
`;

export const ConferenceControllerRedirect: React.FC = () => {
  const router = useRouter();
  const eventId =
    typeof router.query.eventId === 'string'
      ? router.query.eventId
      : Array.isArray(router.query.eventId)
        ? router.query.eventId[0]
        : undefined;
  const { data } = useQuery<
    ConferenceControllerRedirect_Event,
    ConferenceControllerRedirect_EventVariables
  >(EVENT, {
    skip: !router.isReady || !eventId,
    variables: {
      input: {
        _id: eventId ?? '',
      },
    },
  });

  const { hasSessions, hasBooths, hasDescription, hasVisibleSpeakers } =
    useConferenceContentPropertiesExist(
      (data?.event.eventV2?.__typename === 'ConferenceEventV2' &&
        data.event.eventV2) ||
        undefined
    );

  React.useEffect(() => {
    if (!router.isReady || !eventId) return;
    if (hasDescription || hasVisibleSpeakers) {
      router.replace({
        pathname: `/events/conferences/[eventId]/overview`,
        query: router.query,
      });
    } else if (hasSessions) {
      router.replace({
        pathname: `/events/conferences/[eventId]/sessions`,
        query: router.query,
      });
    } else if (hasBooths) {
      router.replace({
        pathname: `/events/conferences/[eventId]/expo`,
        query: router.query,
      });
    } else {
      router.replace({
        pathname: `/events/conferences/[eventId]/overview`,
        query: router.query,
      });
    }
  }, [
    eventId,
    hasBooths,
    hasDescription,
    hasSessions,
    hasVisibleSpeakers,
    router,
  ]);

  return <Loading />;
};
